import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Briefcase,
  ChevronDown,
  CircleUser,
  Home,
  HelpCircle,
  LayoutDashboard,
  LogOut,
  Moon,
  RotateCcw,
  Plus,
  Search,
  Sparkles,
  Sun,
  Upload,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { BRAND_GRADIENT } from '../../lib/brand';
import { authService, UserProfile } from '../../services/auth';
import { stripeService } from '../../services/stripe';
import { gapsService } from '../../services/gaps';
import { SubscriptionDropdown } from '../subscription/SubscriptionDropdown';
import { SubscriptionModal } from '../subscription/SubscriptionModal';
import { JobTailoringPurchaseModal } from '../modals/JobTailoringPurchaseModal';
import { JobUploadModal } from '../modals/JobUploadModal';
import { AuthDialog } from '../auth/AuthDialog';
import { useOnboarding } from '../../hooks/useOnboarding';
import { useDesignSystem } from '../../contexts/DesignSystemContext';

// Recruiter persona nav (spec section 10): Dashboard · Roster · Searches · Import
const RECRUITER_NAV = [
  { path: '/recruiter', label: 'Dashboard', Icon: LayoutDashboard },
  { path: '/recruiter/roster', label: 'Roster', Icon: Users },
  { path: '/recruiter/searches', label: 'Searches', Icon: Search },
  { path: '/recruiter/import', label: 'Import', Icon: Upload },
] as const;

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const { reset, enabled } = useOnboarding();
  const { theme, setTheme } = useDesignSystem();

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Subscription modals
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showJobUploadModal, setShowJobUploadModal] = useState(false);

  // Subscription status for access control
  const [canCreateJob, setCanCreateJob] = useState(false);

  // Portfolio gap count for badge
  const [portfolioGapCount, setPortfolioGapCount] = useState(0);

  const isRecruiter = user?.account_type === 'recruiter';
  const userEmail = user?.email ?? '';
  const userPartner = user?.partner || null;

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const profile = await authService.getCurrentUser();
          setIsAuthenticated(true);
          setUser(profile);

          // Candidate-only chrome: subscription status and portfolio gap badge
          if (profile.account_type !== 'recruiter') {
            loadSubscriptionStatus();
            loadPortfolioGapCount();
          }
        } catch {
          setIsAuthenticated(false);
          authService.logout();
        }
      }
    };
    checkAuth();

    // Listen for gap resolution events (when user resolves gaps)
    const handleGapResolved = () => {
      loadPortfolioGapCount();
    };
    window.addEventListener('gap-resolved', handleGapResolved);

    return () => {
      window.removeEventListener('gap-resolved', handleGapResolved);
    };
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const status = await stripeService.getStatus();
      setCanCreateJob(status.can_create_job || false);
    } catch (error) {
      console.error('Failed to load subscription status:', error);
      setCanCreateJob(false);
    }
  };

  const loadPortfolioGapCount = async () => {
    try {
      const response = await gapsService.getGapsByType('knowledge');
      setPortfolioGapCount(response.total);
    } catch (error) {
      console.error('Failed to load portfolio gap count:', error);
    }
  };

  const handleNewJobClick = () => {
    if (!canCreateJob) {
      // No credits/subscription - show purchase modal
      setShowPurchaseModal(true);
    } else {
      // Has access - show upload modal
      setShowJobUploadModal(true);
    }
  };

  const handleAuthenticated = async () => {
    try {
      const profile = await authService.getCurrentUser();
      setIsAuthenticated(true);
      setUser(profile);
      if (profile.account_type !== 'recruiter') {
        loadSubscriptionStatus();
        loadPortfolioGapCount();
      }
    } catch {
      setIsAuthenticated(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    navigate('/'); // Redirect to landing page after logout
  };

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between">
            {/* Logo - Links to the persona home */}
            <Link to={isRecruiter ? '/recruiter' : '/app'} className="group flex items-center">
              <img
                src={theme === 'light' ? '/career_catalyst_header_light.png' : '/career_catalyst_header.png'}
                alt="Career Catalyst"
                className="h-8 w-auto transition-transform duration-200 group-hover:scale-105"
              />
            </Link>

            {/* Right Side Actions */}
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="ghost"
                aria-label="Toggle theme"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              >
                {theme === 'dark' ? <Sun /> : <Moon />}
              </Button>
              {isAuthenticated ? (
                isRecruiter ? (
                  <>
                    {/* Recruiter persona nav — no candidate credits/portfolio chrome */}
                    {RECRUITER_NAV.map(({ path, label, Icon }) => (
                      <Button key={path} variant="ghost" onClick={() => navigate(path)}>
                        <Icon data-icon="inline-start" />
                        {label}
                      </Button>
                    ))}

                    {/* Account dropdown (recruiter: email + sign out only) */}
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={<Button variant="ghost" aria-label="Account" />}
                      >
                        <CircleUser className="size-5" />
                        <ChevronDown className="size-4 transition-transform group-aria-expanded/button:rotate-180" />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        {userEmail && (
                          <>
                            <div className="px-2.5 py-2">
                              <p className="text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                                Account
                              </p>
                              <p className="mt-1 text-sm font-medium break-all text-foreground">
                                {userEmail}
                              </p>
                            </div>
                            <DropdownMenuSeparator />
                          </>
                        )}
                        <DropdownMenuItem onClick={handleLogout}>
                          <LogOut />
                          Sign Out
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                <>
                  {/* VT Veterans Badge */}
                  {userPartner === 'vt_veterans' && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-success/25 bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
                      <Sparkles className="size-3" />
                      VT Veterans
                    </span>
                  )}

                  <Button variant="ghost" onClick={() => navigate('/app')}>
                    <Home data-icon="inline-start" />
                    Home
                  </Button>

                  <Button
                    variant="ghost"
                    className="relative"
                    onClick={() => navigate('/app/portfolio')}
                  >
                    <Briefcase data-icon="inline-start" />
                    My Portfolio
                    {portfolioGapCount > 0 && (
                      <span
                        className={`absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full bg-warning/80 font-bold text-background ${
                          portfolioGapCount > 9 ? 'text-[8px]' : 'text-[10px]'
                        }`}
                      >
                        {portfolioGapCount > 9 ? '9+' : portfolioGapCount}
                      </span>
                    )}
                  </Button>

                  {/* New Job - the one gradient CTA in the chrome */}
                  <Button
                    onClick={handleNewJobClick}
                    className={cn(BRAND_GRADIENT, "ml-2 text-white shadow-lg shadow-primary/25 hover:shadow-primary/40")}
                  >
                    <Plus data-icon="inline-start" />
                    New Job
                  </Button>

                  {/* Help dropdown with tutorial controls */}
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button size="icon" variant="ghost" aria-label="Help and tutorials" />
                      }
                    >
                      <HelpCircle />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuGroup>
                        <DropdownMenuLabel>
                          Help & Tutorials
                          <div className="text-xs font-normal text-muted-foreground">
                            {enabled ? 'Tips active' : 'Tips disabled'}
                          </div>
                        </DropdownMenuLabel>
                      </DropdownMenuGroup>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          reset();
                          toast.success('Tutorial restarted', {
                            description: 'The tutorial will guide you through the app.',
                          });
                        }}
                      >
                        <RotateCcw />
                        Restart Tutorial
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Profile Dropdown */}
                  <SubscriptionDropdown
                    userEmail={userEmail}
                    onManageClick={() => setShowSubscriptionModal(true)}
                    onSignOut={handleLogout}
                  />
                </>
                )
              ) : (
                <Button onClick={() => setShowLoginModal(true)}>Sign In</Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Login/Register Dialog (shared component) */}
      <AuthDialog
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        onAuthenticated={handleAuthenticated}
      />

      {/* Subscription Management Modals */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onUpgrade={() => {
          setShowSubscriptionModal(false);
          setShowPurchaseModal(true);
        }}
      />

      <JobTailoringPurchaseModal
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        onSuccess={async () => {
          setShowPurchaseModal(false);

          // CRITICAL: Do NOT show upload modal until credit is confirmed in database
          try {
            // Poll backend until credit is available (webhook processed)
            let creditAvailable = false;
            let attempts = 0;
            const maxAttempts = 15; // 15 seconds max

            while (!creditAvailable && attempts < maxAttempts) {
              attempts++;
              await new Promise((resolve) => setTimeout(resolve, 1000));

              // Check if credit exists in database
              const statusData = await stripeService.getStatus();

              if (statusData && (statusData.job_credits > 0 || statusData.can_create_job)) {
                creditAvailable = true;
                setCanCreateJob(statusData.can_create_job);
              }
            }

            if (creditAvailable) {
              // Credit confirmed - reload full status and show upload modal
              await loadSubscriptionStatus();
              window.dispatchEvent(new Event('subscription-updated')); // Notify SubscriptionDropdown to refresh
              setShowJobUploadModal(true);
            } else {
              // Webhook took too long or failed
              toast.error('Payment is processing', {
                description:
                  'This may take a few moments. Please refresh the page and try again in 30 seconds.',
              });
            }
          } catch (error) {
            console.error('Error checking credit availability:', error);
            toast.error('Failed to verify payment', {
              description: 'Please refresh the page and check your credits.',
            });
          }
        }}
      />

      <JobUploadModal
        isOpen={showJobUploadModal}
        onClose={() => setShowJobUploadModal(false)}
        onUpload={async (file) => {
          setShowJobUploadModal(false);
          // Navigate to home page which will handle the upload
          navigate('/app', { state: { uploadFile: file } });
        }}
        onPaste={async (data) => {
          setShowJobUploadModal(false);
          // Navigate to home page which will handle the paste
          navigate('/app', { state: { pasteData: data } });
        }}
      />
    </>
  );
};
